import { useTranslation } from 'react-i18next';
import { ImageSquare } from '@phosphor-icons/react';
import { Typography, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';

import { CarrierLogoBoxStyled } from '../PlanCard/PlanCardStyled';
import {
  getPremiumStyled,
  RETURN_DISPLAY,
  RETURN_VALUE_ONLY,
} from 'enrollment/utils';
import { selectPerPayPeriodText } from 'enrollment/services';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  BenefitNameStyled,
  BkgImageStyled,
  BkgpathStyled,
  CarrierImageStyled,
  ElectedDetailsSectionStyled,
} from './LastYearBenefitsInOeStyled';
import { BenefitDto } from 'theSource/queries';
import { LastYearBenefitPlanDetailsDrawer } from './LastYearBenefitPlanDetailsDrawer';

interface LastYearBenefitsInOeProps {
  queryBenefitsData: BenefitDto[];
  color: string;
  showLastYearBenefitForDualLifeEvent?: boolean;
}

export const LastYearBenefitsInOe = ({
  queryBenefitsData,
  color,
  showLastYearBenefitForDualLifeEvent
}: LastYearBenefitsInOeProps) => {
  const { t } = useTranslation('enrollment.lastYearBenefitInOe');
  const colorNum = color.replace('annotation', '');

  const enrollmentContext = useSelector(selectEnrollmentContext);

  const perPayText = useSelector(selectPerPayPeriodText);

  const isTablet = useMediaQuery('(max-width: 992px)');
  const isMobile = useMediaQuery('(max-width: 743px)');

  const benefitName =
    queryBenefitsData && queryBenefitsData[0]?.coverage?.org_plan_name;

  const displayCarrierLogo = () => {
    return (
      <div>
        {queryBenefitsData[0]?.coverage?.carrier_logo_url ? (
          <CarrierLogoBoxStyled>
            <CarrierImageStyled
              src={queryBenefitsData[0]?.coverage?.carrier_logo_url}
              alt={
                queryBenefitsData[0]?.coverage?.carrier_name ||
                t('enrollment.benefits:defaultLogoAltText')
              }
              data-testid="carrier-logo"
            />
          </CarrierLogoBoxStyled>
        ) : (
          <CarrierLogoBoxStyled>
            <ImageSquare size={32} weight="duotone" color="#56638a" />
            <Typography color="#56638a">
              {queryBenefitsData[0]?.coverage?.carrier_name}
            </Typography>
          </CarrierLogoBoxStyled>
        )}
      </div>
    );
  };

  const showLastYearBenefit = benefitName !== undefined;

  if (!showLastYearBenefit) {
    return null;
  }

  return (
    <div data-testid="lastyearbenefit">
      {(enrollmentContext === 'open' || showLastYearBenefitForDualLifeEvent) &&
        queryBenefitsData &&
        queryBenefitsData.length > 0 &&
        queryBenefitsData[0]?.coverage?.req_coverage_level_lookup_code !==
          'decline' && (
          <div className="card planCard my-5 position-relative d-flex align-items-center">
            <BkgImageStyled
              className="position-absolute"
              data-testid={`bkg-img-${colorNum}`}
            >
              {isMobile ? (
                <svg
                  width="192"
                  height="72"
                  viewBox="0 0 192 72"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  role="presentation"
                >
                  <BkgpathStyled
                    opacity="0.1"
                    d="M-4 -1.99969H191.453C195.008 13.1332 181.028 50.0211 155.636 40.9915C123.896 29.7046 112.449 32.5263 71.6556 54.7867C30.8619 77.047 -1.91869 72.2396 -4 69.8359V-1.99969Z"
                    colorNum={colorNum}
                  />
                </svg>
              ) : (
                <svg
                  width="388"
                  height="139"
                  viewBox="0 0 388 139"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  role="presentation"
                >
                  <BkgpathStyled
                    opacity="0.1"
                    d="M-4 -1.83594H386.447C393.55 27.5784 365.623 99.2789 314.899 81.7278C251.493 59.789 228.626 65.2737 147.134 108.542C65.6421 151.81 0.15774 142.466 -4 137.794V-1.83594Z"
                    colorNum={colorNum}
                  />
                </svg>
              )}
            </BkgImageStyled>
            <div className={`${isMobile ? 'px-3 py-2' : 'px-5 py-3'} w-100`}>
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ minHeight: '40px' }}
              >
                <h2 className="headline-3">
                  {isMobile
                    ? t('mobileElectedLastYear')
                    : t('desktopElectedLastYear')}
                </h2>
                {queryBenefitsData && (
                  <LastYearBenefitPlanDetailsDrawer
                    queryBenefitsData={queryBenefitsData}
                  />
                )}
              </div>
              <ElectedDetailsSectionStyled className="d-flex justify-content-between align-items-center">
                <div>
                  <BenefitNameStyled className={isTablet ? 'mb-n2 mt-2' : ''}>
                    {benefitName}
                  </BenefitNameStyled>
                  {isTablet && displayCarrierLogo()}
                </div>
                <div className="d-flex align-items-center">
                  {!isTablet && displayCarrierLogo()}
                  <div>
                    {queryBenefitsData[0]?.coverage?.subscriber_premium &&
                      (!queryBenefitsData[0].hide_cost_if_empty ||
                        Number(
                          queryBenefitsData[0]?.coverage?.subscriber_premium
                        ) > 0) &&
                      getPremiumStyled({
                        premium: String(
                          queryBenefitsData[0]?.coverage?.subscriber_premium ||
                            0
                        ),
                        valueOnly: isMobile
                          ? RETURN_VALUE_ONLY
                          : RETURN_DISPLAY,
                        perPayText: perPayText,
                        tagName: 'div',
                        dollarClasses: 'headline-3',
                      })}
                  </div>
                </div>
              </ElectedDetailsSectionStyled>
            </div>
          </div>
        )}
    </div>
  );
};
